import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info, Trash2, Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useUserStore } from "@/store/userStore";
import { useFreelancerStore } from "@/store/freelancerStore";
import { format } from "date-fns";

const ProfileEducationForm = () => {
  const { profile, updateFreelancerProfile } = useFreelancerStore();
  const { user } = useUserStore();
  const { toast } = useToast();

  const [education, setEducation] = useState(profile?.education || []);
  const [isAdding, setIsAdding] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [newEducation, setNewEducation] = useState({
    degree: "",
    institution: "",
    year: "",
  });

  useEffect(() => {
    setEducation(profile?.education || []);
  }, [profile]);

  const handleInputChange = (field, value) => {
    setNewEducation((prev) => ({ ...prev, [field]: value }));
  };

  // Add or update education entry
  const handleSaveEducation = async () => {
    const trimmedEducation = {
      degree: newEducation.degree.trim(),
      institution: newEducation.institution.trim(),
      year: newEducation.year.trim(),
    };

    // Skip if all fields are empty
    if (
      !trimmedEducation.degree &&
      !trimmedEducation.institution &&
      !trimmedEducation.year
    ) {
      toast({
        title: "No Data",
        description:
          "Please enter at least one field to save the education entry.",
        variant: "destructive",
      });
      return;
    }

    let updatedEducation;
    if (editIndex !== null) {
      updatedEducation = education.map((item, index) =>
        index === editIndex ? trimmedEducation : item
      );
    } else {
      updatedEducation = [...education, trimmedEducation];
    }

    try {
      await updateFreelancerProfile(
        profile._id,
        { education: updatedEducation },
        user.accessToken
      );
      setEducation(updatedEducation);
      setNewEducation({ degree: "", institution: "", year: "" });
      setIsAdding(false);
      setEditIndex(null);
      toast({
        title: editIndex !== null ? "Education Updated" : "Education Added",
        description: `Successfully ${
          editIndex !== null ? "updated" : "added"
        } education entry.`,
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${
          editIndex !== null ? "update" : "add"
        } education entry. Please try again.`,
        variant: "destructive",
      });
    }
  };

  // Start editing an existing entry
  const handleEditEducation = (index) => {
    setEditIndex(index);
    setNewEducation(education[index]);
    setNewEducation({
      degree: education[index]?.degree,
      institution: education[index]?.institution,
      year: education[index]?.year
        ? format(new Date(education[index]?.year), "yyyy")
        : "",
    });
    setIsAdding(true);
  };

  // Remove an education entry
  const handleRemoveEducation = async (index) => {
    const updatedEducation = education.filter((_, i) => i !== index);
    try {
      await updateFreelancerProfile(
        profile._id,
        { education: updatedEducation },
        user.accessToken
      );
      setEducation(updatedEducation);
      toast({
        title: "Education Removed",
        description: "Successfully removed education entry.",
        variant: "success",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove education entry. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Cancel adding or editing
  const handleCancel = () => {
    setIsAdding(false);
    setEditIndex(null);
    setNewEducation({ degree: "", institution: "", year: "" });
  };

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center">
        <Label className="font-medium text-sm text-gray-700">
          Education (Optional)
        </Label>
        <div className="relative group ml-2">
          <Info className="h-4 w-4 text-gray-400" />
          <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
            Add your educational background, including degree, institution, and
            year of graduation. (Optional)
          </div>
        </div>
      </div>

      {isAdding ? (
        <div className="border p-4 rounded-md space-y-4">
          <div className="space-y-2">
            <Label htmlFor="degree">Degree</Label>
            <Input
              id="degree"
              placeholder="e.g., B.Sc. in Computer Science"
              value={newEducation.degree}
              onChange={(e) => handleInputChange("degree", e.target.value)}
              className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="institution">Institution</Label>
            <Input
              id="institution"
              placeholder="e.g., University of California"
              value={newEducation.institution}
              onChange={(e) => handleInputChange("institution", e.target.value)}
              className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="year">Year</Label>
            <Input
              id="year"
              placeholder="e.g., 2018"
              value={newEducation.year}
              onChange={(e) => handleInputChange("year", e.target.value)}
              className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
            />
          </div>

          <div className="flex space-x-2">
            <Button
              onClick={handleSaveEducation}
              className="bg-purple-500 text-white hover:bg-purple-600"
            >
              {editIndex !== null ? "Update" : "Add"}
            </Button>
            <Button
              onClick={handleCancel}
              className="bg-gray-500 text-white hover:bg-gray-600"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : education.length === 0 ? (
        <Button
          onClick={() => setIsAdding(true)}
          className="bg-purple-500 text-white hover:bg-purple-600"
        >
          Add Education
        </Button>
      ) : (
        <Button
          onClick={() => setIsAdding(true)}
          className="bg-purple-500 text-white hover:bg-purple-600"
        >
          <Plus className="h-6 w-6" />
        </Button>
      )}

      {education.length > 0 && (
        <div className="space-y-4">
          {education.map((entry, index) => (
            <div
              key={index}
              className="border p-4 rounded-md space-y-2 bg-gradient-to-r from-amber-50/50 to-orange-50/50"
            >
              <p className="text-sm font-medium">
                {entry.degree || "No degree specified"}
              </p>
              <p className="text-sm text-gray-600">
                {entry.institution || "No institution specified"}
              </p>
              <p className="text-sm text-gray-600">
                {entry?.year
                  ? format(new Date(entry?.year), "yyyy")
                  : "No year specified"}
              </p>
              <div className="flex space-x-2 mt-2">
                <Button
                  variant="outline"
                  onClick={() => handleEditEducation(index)}
                  aria-label="Edit education entry"
                >
                  <Pencil className="h-3 w-3 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleRemoveEducation(index)}
                  aria-label="Remove education entry"
                >
                  <Trash2 className="h-3 w-3" />
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileEducationForm;
